import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { StepsContext } from './steps-context';
import { isInstanceOfStepContainer } from './is-instance-of';
import { getStepsTree } from './get-steps-tree';

/**
 * Perform a breadth-first search and return the first descendant that satisifies the predicate.
 * @param {ReactNode|ReactNode[]} children - Array of children to search through.
 * @param {Function} predicate - Function that takes in a ReactNode and returns true or false.
 * @returns {ReactNode} - First descendant in breadth-first search order to satisfy the predicate.
 */
function breadthFirstSearch(children, predicate) {
  const queue = [];
  React.Children.forEach(children, (child) => {
    queue.push(child);
  });

  while (queue.length > 0) {
    const current = queue.shift();
    if (predicate(current)) {
      return current;
    }

    if (current.props && current.props.children) {
      React.Children.forEach(current.props.children, (child) => {
        queue.push(child);
      });
    }
  }

  return null;
}

/**
 * Provides information about the current step to render and the tree view of all steps.
 * Used by <StepProgressList /> to display the list of all steps, used by <Step /> to determine if
 * it should render.
 * @param {*} props - see propTypes definition.
 * @returns {ReactNode} - Context provider.
 */
export function StepsContextProvider(props) {
  const { currentPath, children } = props;

  const rootStepContainerChildren = useMemo(() => {
    // search for first <StepContainer /> instance
    const rootStepContainer = breadthFirstSearch(children, isInstanceOfStepContainer);

    // return the children of the first step container instance
    return rootStepContainer && rootStepContainer.props && rootStepContainer.props.children;
  }, [children]);

  // build the step tree
  const stepsContext = {
    currentPath,
    ...getStepsTree(rootStepContainerChildren, currentPath),
  };

  return (
    <StepsContext.Provider value={stepsContext}>
      {children}
    </StepsContext.Provider>
  );
}

StepsContextProvider.propTypes = {
  /**
   * Path to the currently selected step node.
   */
  currentPath: PropTypes.string.isRequired,

  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};
